import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const steps = [
  {
    icon: 'ri-user-search-line',
    title: 'Explora Mentores',
    description: 'Navega por nuestro catálogo de expertos de la industria y encuentra el mentor que mejor se adapte a tus intereses y objetivos profesionales.'
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Regístrate',
    description: 'Ingresa a la sesion general, dentro de ella podrás coversar con los mentores, conocer sus perfiles y agendar sesiones de mentoría personalizadas.'
  },
  {
    icon: 'ri-video-chat-line',
    title: 'Conéctate en Vivo',
    description: 'Participa en una sesión dinámica e interactiva donde podrás hacer preguntas, recibir consejos y obtener perspectivas valiosas.'
  },
  {
    icon: 'ri-rocket-line',
    title: 'Impulsa tu Carrera',
    description: 'Aplica los conocimientos adquiridos, expande tu red profesional y da el siguiente paso en tu desarrollo profesional.'
  }
];

function Step({ step, index }: { step: typeof steps[0], index: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px 0px" });

  const variants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <motion.div
      ref={ref}
      variants={variants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      transition={{ duration: 0.5, delay: index * 0.2 }}
      className="relative flex w-full my-4"
    >
      {/* Timeline line */}
      <div className="absolute left-5 top-0 h-full w-0.5 bg-gray-200"></div>

      {/* Step Number Circle */}
      <div className="absolute left-5 -translate-x-1/2 w-12 h-12 bg-[#bb8800] text-white rounded-full flex items-center justify-center font-bold text-xl shadow-md z-10 border-4 border-white">
        {index + 1}
      </div>

      {/* Content Card */}
      <div className="w-full pl-16">
        <div className="p-6 rounded-2xl border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center gap-4 mb-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#1b3d70] to-[#2a5490] rounded-xl flex items-center justify-center flex-shrink-0">
              <i className={`${step.icon} text-2xl text-white`}></i>
            </div>
            <h3 className="text-xl font-bold text-[#1b3d70]">
              {step.title}
            </h3>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">
            {step.description}
          </p>
        </div>
      </div>
    </motion.div>
  );
}


export default function HowItWorks() {
  return (
    <section id="como-funciona" className="py-24 bg-gray-50 overflow-hidden">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 md:mb-20">
          <motion.h2 
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-4xl sm:text-5xl font-bold text-[#1b3d70] mb-4"
          >
            ¿Cómo Funciona?
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg text-gray-600 max-w-2xl mx-auto"
          >
            Cuatro pasos sencillos para conectarte con expertos de la industria y acelerar tu desarrollo profesional.
          </motion.p>
        </div>

        <div className="relative flex flex-col">
          {steps.map((step, index) => (
            <Step key={index} step={step} index={index} />
          ))}
        </div>

        {/* CTA */}
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: steps.length * 0.2 }}
          className="text-center mt-16"
        >
          <button 
            onClick={() => {
              const element = document.getElementById('mentores');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-[#1b3d70] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#2a5490] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
          >
            Comenzar Ahora
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
        </motion.div>
      </div>
    </section>
  );
}